import { faEdit } from "@fortawesome/free-regular-svg-icons";
import { faPlus, faSpinner, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { JSX, useMemo, useState } from "react";
import { Popover, ArrowContainer, PopoverPosition } from "react-tiny-popover";
import styled from "styled-components";
import {
  deletePage,
  getPage,
  pageMetaListToTree,
  PageTreeItem,
  setPage,
  slugize,
  usePageMeta,
} from "./api";
import { ComponentSpec, ComponentTypes } from "./components";
import { IComponent } from "./components/Component";
import { Link } from "../utils/router";
import { newEmptyVertical } from "./components/Vertical";
import {
  BarButton,
  BarInput,
  BarSelectProperty,
  BarSelectOption,
  InputLabel,
  BarPropContainer,
  SelectInput,
  SectionTitle,
} from "./components/PropertiesBar";
import { Container, Row, Col, Alert } from "react-bootstrap";
import { useUser } from "../utils/auth";

const PAGE_HOME = "page_home";
const PAGE_NORMAL = "page_normal";
const PAGE_REDIRECT = "page_redirect";

const CreatePopoverContainer = styled.div`
  padding: 10px;
  background-color: #1f2427;
  color: white;
  box-shadow: 0 0 12px 0px #1f1c1c;
  border-radius: 5px;
`;

const PageControlContainer = styled.span`
  display: inline-block;
  padding-left: 8px;
`;

const EditIcon = styled(FontAwesomeIcon)`
  margin-right: 8px;
  color: #0080ff;
  cursor: pointer;
  &:hover {
    color: #1d77d0;
  }
  &:active {
    color: #3e9dfb;
  }
`;

const ConfimText = styled.code`
  background-color: #3a4348;
  padding: 2px;
  color: white;
`;

// Style components for better hierarchy visualization
const PageItem = styled.li<{ isEmptyParent?: boolean }>`
  position: relative;
  margin: 4px 0;
  padding-left: 20px;

  &::before {
    content: "•";
    color: ${(props) => (props.isEmptyParent ? "#9e9e9e" : "#0080ff")};
    position: absolute;
    left: 0;
    font-size: 16px;
    top: 2px; /* Fixed position relative to the top for consistent alignment */
    transform: none;
  }
`;

const PageItemWithChildren = styled(PageItem)`
  &::before {
    content: "▶";
    font-size: 10px;
    color: ${(props) => (props.isEmptyParent ? "#9e9e9e" : "#0080ff")};
    top: 3px; /* Aligned with the title */
    transform: none;
  }
`;

const LevelConnector = styled.div`
  position: absolute;
  left: 5px;
  top: 20px;
  bottom: 0;
  width: 1px;
  background: #ddd;
`;

const ChildrenList = styled.ul`
  list-style-type: none;
  padding-left: 20px;
  // margin-top: 8px;
  margin-left: 1px;
  border-left: 1px dashed #ddd;
  position: relative;
`;

const PageTitle = styled(Link)<{ isParent?: boolean }>`
  display: inline-block;
  vertical-align: middle;
  margin-right: 5px;
  font-size: 1rem;
  //font-size: ${(props) => (props.isParent ? "1rem" : "0.95rem")};
  // font-weight: ${(props) => (props.isParent ? "500" : "normal")};
`;

// Create a styled component for empty parent pages (not clickable)
const EmptyParentTitle = styled.span<{ isParent?: boolean }>`
  display: inline-block;
  vertical-align: middle;
  margin-right: 5px;
  font-size: 1rem;
  color: #9e9e9e; /* Grey color for visual distinction */
  cursor: default; /* Non-clickable cursor */
`;

function RenderRecursive(props: { page: PageTreeItem; user?: any }) {
  const [isOpen, setOpen] = useState(false);
  const [popoverContent, setPopoverContent] = useState<
    "Create" | "Modify" | "Delete"
  >("Create");

  const hasChildren = props.page.children.length > 0;
  const ItemComponent = hasChildren ? PageItemWithChildren : PageItem;

  // Determine if this is an empty parent page (auto-generated with accessPolicy === null)
  const isEmptyParent = props.page.generated === true;

  return (
    <ItemComponent isEmptyParent={isEmptyParent}>
      {isEmptyParent ? (
        // Empty parent pages are non-clickable and greyed out
        <EmptyParentTitle isParent={hasChildren}>
          {props.page.title}
        </EmptyParentTitle>
      ) : (
        // Real pages are clickable
        <PageTitle href={props.page.path!} isParent={hasChildren}>
          {props.page.title}
        </PageTitle>
      )}
      {props.page.accessPolicy !== null ||
      (isEmptyParent && props.page.path && props.user) ? (
        <Popover
          isOpen={isOpen}
          onClickOutside={() => {
            setOpen(false);
          }}
          positions={["bottom"]}
          padding={10}
          containerStyle={{ overflow: "visible" }}
          content={({
            position,
            childRect,
            popoverRect,
          }: {
            position: PopoverPosition;
            childRect: DOMRect;
            popoverRect: DOMRect;
          }): JSX.Element => (
            <ArrowContainer
              position={position}
              childRect={childRect}
              popoverRect={popoverRect}
              arrowColor={"#1f2427"}
              arrowSize={10}
            >
              {popoverContent === "Create" ? (
                <CreateNewPage page={props.page} setOpen={setOpen} />
              ) : popoverContent === "Modify" ? (
                <ModifyPage page={props.page} setOpen={setOpen} />
              ) : popoverContent === "Delete" ? (
                <DeletePage page={props.page} setOpen={setOpen} />
              ) : (
                <></>
              )}
            </ArrowContainer>
          )}
        >
          <span>
            <PageControlContainer>
              <EditIcon
                size="xs"
                icon={faPlus}
                onClick={() => {
                  setPopoverContent("Create");
                  setOpen(true);
                }}
              />
              {!isEmptyParent && (
                <>
                  <EditIcon
                    size="xs"
                    icon={faEdit}
                    onClick={() => {
                      setPopoverContent("Modify");
                      setOpen(true);
                    }}
                  />
                  <EditIcon
                    size="xs"
                    icon={faTrash}
                    onClick={() => {
                      setPopoverContent("Delete");
                      setOpen(true);
                    }}
                  />
                </>
              )}
            </PageControlContainer>
          </span>
        </Popover>
      ) : null}
      {hasChildren && (
        <ChildrenList>
          {props.page.children.map((child: any, index) => (
            <RenderRecursive key={index} page={child} user={props.user} />
          ))}
        </ChildrenList>
      )}
    </ItemComponent>
  );
}

function CreateNewPage(props: {
  page: PageTreeItem;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}): JSX.Element {
  const [newPageText, setNewPageText] = useState("");
  const [newPageType, setNewPageType] = useState<ComponentTypes>(PAGE_NORMAL);
  // const [newRedirectURL, setNewRedirectURL] = useState("");

  const pageTypeOptions: BarSelectOption[] = [
    { value: PAGE_NORMAL, label: "Page Layout Normal" },
    { value: PAGE_HOME, label: "Page Layout with Image" },
    { value: PAGE_REDIRECT, label: "Redirect" },
  ];

  return (
    <CreatePopoverContainer>
      <SectionTitle>Add new page under {props.page.title}</SectionTitle>

      <BarPropContainer>
        <InputLabel>Page Name:</InputLabel>
        <BarInput
          placeholder="New Page Name"
          value={newPageText}
          onChange={(e) => setNewPageText(e.currentTarget.value)}
        />
      </BarPropContainer>

      <BarSelectProperty
        name="Page Type"
        value={newPageType}
        options={pageTypeOptions}
        onChange={(value) => setNewPageType(value as ComponentTypes)}
      />

      {/* {newPageType === PAGE_REDIRECT ? (
        <div>
          <InputLabel>Redirect URL:</InputLabel>
          <BarInput
            placeholder="Redirect URL"
            value={newRedirectURL}
            onChange={(e) => setNewRedirectURL(e.currentTarget.value)}
            onBlur={() => {
              if (!isGoodURL(newRedirectURL)) {
                alert("Invalid URL entered");
                setNewRedirectURL("");
              }
            }}
          />
        </div>
      ) : null} */}

      <BarButton
        onClick={async () => {
          const content: ComponentSpec =
            newPageType === PAGE_HOME
              ? {
                  type: PAGE_HOME,
                  content: newEmptyVertical(),
                }
              : {
                  type: PAGE_NORMAL,
                  content: newEmptyVertical(),
                };
          // : {
          //     type: PAGE_REDIRECT,
          //     redirectURL: newRedirectURL,
          //   };
          await setPage({
            page: {
              accessPolicy: props.page.accessPolicy || {
                allowViewFromGroups: [],
                allowEditFromGroups: [],
              },
              title: newPageText,
              content,
              description: "",
              publishTime: Math.floor(new Date().valueOf() / 1000),
              revision: 0,
            },
            path: props.page.path?.endsWith("/")
              ? props.page.path! + slugize(newPageText)
              : props.page.path! + "/" + slugize(newPageText),
          });
          setNewPageText("");
          setNewPageType(PAGE_HOME);
          props.setOpen(false);
        }}
      >
        Create
      </BarButton>
    </CreatePopoverContainer>
  );
}

function ModifyPage(props: {
  page: PageTreeItem;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}): JSX.Element {
  const [newPageText, setNewPageText] = useState(props.page.title);
  const [newPageType, setNewPageType] = useState<ComponentTypes>(
    props.page.pageType ?? PAGE_NORMAL
  );

  // Get parent path to use for the custom path
  const parentPath = props.page.path?.endsWith("/")
    ? props.page.path!.substring(
        0,
        props.page.path!.slice(0, -1).lastIndexOf("/") + 1
      )
    : props.page.path!.substring(0, props.page.path!.lastIndexOf("/") + 1);

  // Extract current slug from path
  const currentSlug = props.page.path?.endsWith("/")
    ? props.page
        .path!.slice(0, -1)
        .substring(props.page.path!.slice(0, -1).lastIndexOf("/") + 1)
    : props.page.path!.substring(props.page.path!.lastIndexOf("/") + 1);

  const [customPath, setCustomPath] = useState(currentSlug);
  const [fullPath, setFullPath] = useState(props.page.path || "/");
  const [pathEditMode, setPathEditMode] = useState<"none" | "segment" | "full">(
    "none"
  );

  // const [newRedirectURL, setNewRedirectURL] = useState(props.page.redirectURL);

  const pageTypeOptions: BarSelectOption[] = [
    { value: PAGE_NORMAL, label: "Page Layout Normal" },
    { value: PAGE_HOME, label: "Page Layout with Image" },
    { value: PAGE_REDIRECT, label: "Redirect" },
  ];

  return (
    <CreatePopoverContainer>
      <SectionTitle>Modify page: {props.page.title}</SectionTitle>

      <BarPropContainer>
        <InputLabel>Page Name:</InputLabel>
        <BarInput
          placeholder="Page Name"
          value={newPageText}
          onChange={(e) => setNewPageText(e.currentTarget.value)}
        />
      </BarPropContainer>

      <BarPropContainer>
        <div style={{ marginBottom: "8px" }}>
          <div style={{ display: "flex", marginBottom: "8px" }}>
            <input
              type="radio"
              id="pathEditNone"
              name="pathEditMode"
              checked={pathEditMode === "none"}
              onChange={() => setPathEditMode("none")}
              style={{ marginRight: "8px" }}
            />
            <label
              htmlFor="pathEditNone"
              style={{ margin: 0, cursor: "pointer" }}
            >
              Auto-generate path from title
            </label>
          </div>

          <div style={{ display: "flex", marginBottom: "8px" }}>
            <input
              type="radio"
              id="pathEditSegment"
              name="pathEditMode"
              checked={pathEditMode === "segment"}
              onChange={() => setPathEditMode("segment")}
              style={{ marginRight: "8px" }}
            />
            <label
              htmlFor="pathEditSegment"
              style={{ margin: 0, cursor: "pointer" }}
            >
              Edit path segment (keep parent)
            </label>
          </div>

          <div style={{ display: "flex", marginBottom: "8px" }}>
            <input
              type="radio"
              id="pathEditFull"
              name="pathEditMode"
              checked={pathEditMode === "full"}
              onChange={() => setPathEditMode("full")}
              style={{ marginRight: "8px" }}
            />
            <label
              htmlFor="pathEditFull"
              style={{ margin: 0, cursor: "pointer" }}
            >
              Edit full path (move page)
            </label>
          </div>
        </div>

        {pathEditMode === "segment" && (
          <>
            <InputLabel>Custom Path Segment:</InputLabel>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={{ color: "#adadad", marginRight: "5px" }}>
                {parentPath}
              </span>
              <BarInput
                placeholder="custom-path"
                value={customPath}
                onChange={(e) => setCustomPath(e.currentTarget.value)}
              />
            </div>
          </>
        )}

        {pathEditMode === "full" && (
          <>
            <InputLabel>Full Path:</InputLabel>
            <BarInput
              placeholder="/path/to/page"
              value={fullPath}
              onChange={(e) => setFullPath(e.currentTarget.value)}
            />
            <div
              style={{ color: "#adadad", fontSize: "12px", marginTop: "5px" }}
            >
              Must start with / and include all parent paths
            </div>
          </>
        )}
      </BarPropContainer>

      <BarSelectProperty
        name="Page Type"
        value={newPageType}
        options={pageTypeOptions}
        onChange={(value) => setNewPageType(value as ComponentTypes)}
      />

      {/* {newPageType === PAGE_REDIRECT ? (
        <div>
          <InputLabel>Redirect URL:</InputLabel>
          <BarInput
            placeholder="Redirect URL"
            value={newRedirectURL as string}
            onChange={(e) => setNewRedirectURL(e.currentTarget.value)}
            onBlur={() => {
              if (!isGoodURL(newRedirectURL ?? "")) {
                alert("Invalid URL entered");
                setNewRedirectURL("");
              }
            }}
          />
        </div>
      ) : null} */}

      <BarButton
        onClick={async () => {
          if (typeof props.page.path !== "string") {
            console.log("paht not a string");
            return;
          }

          await getPage({ path: props.page.path }).then(async (page: any) => {
            page.content.type = newPageType;
            // if (newPageType === PAGE_REDIRECT) {
            //   const content: ComponentSpec = {
            //     type: PAGE_REDIRECT,
            //     redirectURL:
            //       newRedirectURL?.length === 0 ? "/" : newRedirectURL ?? "/",
            //   };

            //   page.content = content;
            // }

            page.title = newPageText;

            // Determine the new path based on user preferences
            let newPath;
            if (pathEditMode === "segment") {
              // Use the custom path segment provided by the user
              newPath = parentPath + customPath;
            } else if (pathEditMode === "full") {
              // Use the full custom path (allows moving pages between parents)
              // Ensure path starts with /
              newPath = fullPath.startsWith("/") ? fullPath : "/" + fullPath;
              // Remove trailing slash if present, except for root
              if (newPath !== "/" && newPath.endsWith("/")) {
                newPath = newPath.slice(0, -1);
              }
            } else {
              // Use automatic path based on the title (default)
              newPath = parentPath + slugize(newPageText);
            }

            if (newPath !== props.page.path) {
              await setPage({
                page: page,
                path: newPath,
              }).then(async () => {
                await deletePage({ path: props.page.path! });
              });
            } else {
              await setPage({
                page: page,
                path: props.page.path!,
              });
            }
          });

          props.setOpen(false);
        }}
      >
        Update
      </BarButton>
    </CreatePopoverContainer>
  );
}

// Create a styled component for error messages
const ErrorText = styled.p`
  color: #ff6b6b;
  font-size: 13px;
  margin-top: 8px;
`;

// Create a danger button variant
const DangerButton = styled(BarButton)`
  background: #dc3545;
  :hover {
    background: #c82333;
  }
  :active {
    background: #bd2130;
  }
`;

function DeletePage(props: {
  page: PageTreeItem;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}): JSX.Element {
  const [newPageText, setNewPageText] = useState("");
  const [errorText, setErrorText] = useState("");

  return (
    <CreatePopoverContainer>
      <SectionTitle>Delete Page</SectionTitle>
      <p style={{ color: "#adadad", fontSize: "14px" }}>
        Are you absolutely sure you want to delete this page?
      </p>
      <p style={{ color: "#adadad", fontSize: "14px" }}>
        Enter the following to confirm:{" "}
        <ConfimText>{props.page.title}</ConfimText>
      </p>

      <BarPropContainer>
        <InputLabel>Confirmation:</InputLabel>
        <BarInput
          placeholder={props.page.title}
          value={newPageText}
          onChange={(e) => setNewPageText(e.currentTarget.value)}
        />
      </BarPropContainer>

      <DangerButton
        onClick={async () => {
          if (props.page.title !== newPageText) {
            setErrorText("Page name does not match");
            console.log("error");
            return;
          }

          if (typeof props.page.path !== "string") {
            console.log("paht not a string");
            return;
          }

          await deletePage({
            path: props.page.path,
          });

          setNewPageText("");
          props.setOpen(false);
        }}
      >
        Delete
      </DangerButton>

      {errorText === "" ? null : <ErrorText>Error: {errorText}</ErrorText>}
    </CreatePopoverContainer>
  );
}

const siteMapRequest = { includeContent: false, prefix: "/" };

export const SITE_MAP = "site_map";

export interface SiteMapSpec {
  type: typeof SITE_MAP;
}

const SiteMapTitle = styled.h2`
  font-size: 1.5em;
  margin-bottom: 15px;
  color: #333;
  font-weight: 600;
`;

const SiteMapList = styled.ul`
  padding-left: 10px;
  list-style-type: none;
  margin-top: 15px;
`;

// Card-style container for the site map
const SiteMapCard = styled.div`
  background: white;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 20px;
`;

// Legend component to explain the hierarchy markers
const HierarchyLegend = styled.div`
  background: #f8f9fa;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 15px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;

  & > div {
    margin-right: 15px;
    display: flex;
    align-items: center;
  }
`;

const LegendIcon = styled.span<{ fontSize?: string }>`
  color: #0080ff;
  margin-right: 5px;
  font-size: ${(props) => props.fontSize || "16px"};
`;

export function SiteMap(props: { spec: SiteMapSpec }) {
  const user = useUser();
  const pageMetaStatus = usePageMeta(siteMapRequest);
  const pageTree = useMemo(
    () =>
      pageMetaStatus.pages === null
        ? null
        : pageMetaListToTree(pageMetaStatus.pages!),
    [pageMetaStatus.pages]
  );

  if (pageMetaStatus.loading) {
    return (
      <Container className="mt-4">
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8}>
            <div className="text-center py-5">
              <FontAwesomeIcon
                spin
                icon={faSpinner}
                size="2x"
                style={{ color: "#0080ff" }}
              />
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  if (pageMetaStatus.error !== null) {
    return (
      <Container className="mt-4">
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8}>
            <ErrorText>{pageMetaStatus.error.toString()}</ErrorText>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={8}>
          <SiteMapCard>
            <SiteMapTitle>Site Map</SiteMapTitle>

            {user !== null ? (
              <>
                <HierarchyLegend>
                  <div>
                    <LegendIcon>•</LegendIcon>
                    <span>Page</span>
                  </div>
                  <div>
                    <LegendIcon fontSize="10px">▶</LegendIcon>
                    <span>Parent Page</span>
                  </div>
                  <div>
                    <LegendIcon>︱</LegendIcon>
                    <span>Children</span>
                  </div>
                  <div>
                    <span style={{ color: "#9e9e9e", marginRight: "5px" }}>
                      Text
                    </span>
                    <span>Auto-generated Parent (non-clickable)</span>
                  </div>
                </HierarchyLegend>
                <Alert variant="warning">
                  Das Umbenennen von Seiten oder das Anpassen der URL kann zu
                  defekten Links führen. Suchmaschinen können falsche Ergebnisse
                  anzeigen.
                </Alert>
              </>
            ) : null}

            <SiteMapList>
              <RenderRecursive page={pageTree!} user={user} />
            </SiteMapList>
          </SiteMapCard>
        </Col>
      </Row>
    </Container>
  );
}

export const SiteMapComponent: IComponent<SiteMapSpec> = {
  component: SiteMap,
};
